import { useTranslation } from 'react-i18next';

import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { isDefined } from 'support/helpers/generic/generic';
import { isMessageV2DTO } from 'support/helpers/transactions/transactions';
import { MessageV2DTO, PartnerDTO, SenderEnvelopeDTO, TransactionOriginApp } from 'support/types';
import { DocumentEnvironmentBadge } from 'pages/Transactions/components/DocumentEnvironmentBadge/DocumentEnvironmentBadge';
import { Button } from 'components/Form/Button';
import { TradePartnerNameAndLogo } from 'components/TradePartnerNameAndLogo';
import { TransactionStatusDisplay } from 'components/TransactionStatusDisplay/TransactionStatusDisplay';

import { DocumentProperties, DocumentPropertiesItem } from '../../../../Sidebar/SidebarProperties/SidebarProperties';
import { LastEditBy } from './components/LastEditBy/LastEditBy';
import { hasProofOfDelivery, ProofOfDelivery } from './components/ProofOfDelivery/ProofOfDelivery';
import { useDocumentSource } from './useDocumentSource';
import { useIssueResolverName } from './useIssueResolverName';
import { WebEDIDocumentPropertiesKeys } from './WebEDIDocumentPropertiesKeys';

type WebEDIDocumentPropertiesProps = {
  partner: PartnerDTO | undefined | null;
  document: MessageV2DTO | SenderEnvelopeDTO | undefined;
  documentOriginApp: TransactionOriginApp;
  currentPartner: PartnerDTO | undefined;
  onViewPDF?: (url: string, filename: string) => void;
};

export const WebEDIDocumentProperties = ({
  partner,
  document,
  documentOriginApp,
  currentPartner,
  onViewPDF,
}: WebEDIDocumentPropertiesProps) => {
  const { t } = useTranslation();
  const isMessage = document && isMessageV2DTO(document);
  const isSenderEnvelope = document && !isMessageV2DTO(document);

  const issueResolverName = useIssueResolverName(
    isMessage
      ? { isMessage: true, document, currentPartner, partner }
      : { isMessage: false, document, currentPartner, partner },
  );

  const documentSourceValue = useDocumentSource({ document, documentOriginApp });

  const issueResolverItem = issueResolverName
    ? {
        label: t('webedi:sidebar.properties.issueResolver'),
        key: WebEDIDocumentPropertiesKeys.ISSUE_RESOLVER,
        value: issueResolverName,
      }
    : null;

  const messageFlowItem =
    isMessage && document?.flow === 'TEST'
      ? {
          label: t('webedi:sidebar.properties.environment'),
          key: WebEDIDocumentPropertiesKeys.ENVIRONMENT,
          value: <DocumentEnvironmentBadge flow={document.flow} />,
        }
      : null;

  const proofOfDeliveryItem =
    isMessage && hasProofOfDelivery(document)
      ? {
          label: t('webedi:proofOfDelivery.label'),
          key: WebEDIDocumentPropertiesKeys.PROOF_OF_DELIVERY,
          value: (
            <ProofOfDelivery
              receiverEnvelope={{
                proofOfDelivery: document.receiver_envelope.proofOfDelivery,
                receiverIntegration: document.receiver_envelope.receiverIntegration,
              }}
            />
          ),
        }
      : null;

  const editInformation =
    isMessage && document.latest_user_update
      ? {
          label: t('webedi:sidebar.properties.editInformation.editedAt.label'),
          key: WebEDIDocumentPropertiesKeys.EDIT_INFORMATION,
          value: <LastEditBy latestUpdate={document.latest_user_update} />,
        }
      : null;

  const documentSource = documentSourceValue
    ? {
        label: t('webedi:sidebar.properties.documentSource'),
        key: WebEDIDocumentPropertiesKeys.DOCUMENT_SOURCE,
        value: documentSourceValue,
      }
    : null;

  const firstAttachment = isMessage ? document.attachments?.[0] : undefined;
  const pdfUrl = firstAttachment?.url ?? '';
  const pdfFilename = firstAttachment?.name ?? '';

  const hasOriginalPDF =
    isMessage && (documentOriginApp === 'DOCUMENT_UNDERSTANDING_AGENT' || documentOriginApp === 'UNATTENDED_OCR');

  const originalPDF =
    hasOriginalPDF && onViewPDF
      ? {
          label: t('webedi:sidebar.properties.originalPDF'),
          key: WebEDIDocumentPropertiesKeys.ORIGINAL_PDF,
          value: (
            <Button
              type="button"
              variant="secondary"
              size="extra-small"
              analyticsId="transactions:view_original_pdf"
              onClick={() => onViewPDF(pdfUrl, pdfFilename)}
            >
              {t('webedi:sidebar.properties.viewPDF')}
            </Button>
          ),
        }
      : null;

  const properties: Array<DocumentPropertiesItem | null> = [
    {
      label: t('webedi:sidebar.properties.partner'),
      key: WebEDIDocumentPropertiesKeys.PARTNER,
      value: (
        <TradePartnerNameAndLogo
          partner={partner}
          size="small"
          textConfiguration={{ className: 'text-inherit' }}
          logoConfiguration={{ className: 'rounded-full' }}
        />
      ),
    },
    {
      label: t('webedi:sidebar.properties.status'),
      key: WebEDIDocumentPropertiesKeys.STATUS,
      value: (
        <TransactionStatusDisplay
          transaction={isMessage ? document : undefined}
          state={isSenderEnvelope ? document?.state : undefined}
          currentPartner={currentPartner}
        />
      ),
    },
    issueResolverItem,
    messageFlowItem,
    {
      label: t('webedi:sidebar.properties.documentDate'),
      key: WebEDIDocumentPropertiesKeys.DOCUMENT_DATE,
      value: isMessage
        ? formatDayAndTime(document.created_at)
        : isSenderEnvelope
          ? formatDayAndTime(document.createdAt)
          : EMPTY_FIELD,
    },
    documentSource,
    originalPDF,
    editInformation,
    proofOfDeliveryItem,
  ];

  return <DocumentProperties properties={properties.filter(isDefined)} />;
};
